import {
  ConfigApiError,
  type ConfigError,
  type EntityDetailError,
  jsonPointerToFormPath,
  jsonPointerToInstancePath,
  mapStatusToErrorType,
} from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import {
  AdditionalValidationErrorKey,
  useConfigUserFeedback,
} from '../../state/config-user-feedback-state/config-user-feedback-state';
import { buildErrorSummaryParts } from '../../utilities/build-error-summary-parts/build-error-summary-parts';

/**
 * Provides an error handler function for entity operations.
 *
 * Stores raw errors only. The `detail` keeps its `{{...}}` placeholders
 * intact and `formPath` carries the dot-separated path derived from the
 * backend pointer. Pages with entity context (entity-details) resolve
 * placeholders and compute a navigable `path` at render time; pages
 * without (admin tools, custom action handlers) render the raw text
 * with no link.
 */
export const useEntityOperationError = () => {
  const { setConfigError, setShowErrorBanner, setAdditionalValidationErrors } =
    useConfigUserFeedback((state) => ({
      setConfigError: state.setConfigError,
      setShowErrorBanner: state.setShowErrorBanner,
      setAdditionalValidationErrors: state.setAdditionalValidationErrors,
    }));

  const { formatMessage } = useIntl();

  const handleEntityOperationError = (operationApiError: ConfigApiError) => {
    const errors: EntityDetailError[] | undefined =
      operationApiError.errors?.map((error) => {
        const trimmedDetail = error.detail?.trim();
        const detail =
          trimmedDetail && trimmedDetail !== ''
            ? error.detail
            : formatMessage({ id: 'NO_SERVICE_ERROR_DETAIL' });

        return {
          detail,
          secondaryDetail:
            buildErrorSummaryParts(error, formatMessage) ||
            operationApiError.message,
          formPath: jsonPointerToFormPath(error.source?.pointer),
          fieldPath: jsonPointerToInstancePath(error.source?.pointer),
        };
      });

    const configError: ConfigError = {
      errors,
      type: mapStatusToErrorType(operationApiError.status),
      message: operationApiError.message,
      status: operationApiError.status,
      name: operationApiError.name,
    };

    setConfigError(configError);
    setShowErrorBanner(true);

    // Mirror the errors into `additionalValidationErrors` so the
    // entity-details page can resolve them at render time and pass
    // them to JSON Forms via its `additionalErrors` prop. Errors
    // without a `fieldPath` aren't useful for inline rendering and
    // are filtered out (they still surface in the banner via
    // `configError`). Each new failed save replaces this slice; a
    // successful save clears it via `resetConfigUserFeedbackStore`.
    const apiValidationErrors = (errors ?? []).filter((error) =>
      Boolean(error.fieldPath),
    );
    setAdditionalValidationErrors(
      AdditionalValidationErrorKey.ApiValidation,
      apiValidationErrors,
    );
  };

  return handleEntityOperationError;
};
